/**
 * <mail-it-for-you>  –  Handwritten Postcards Module
 *
 * Custom element that manages:
 *  • 3-way radio switching (printed / digital / mail-it)
 *  • Package checkbox selection
 *  • Per-package message type (default vs custom)
 *  • Live address preview
 *  • Totals calculation
 *  • Validation & Add-to-Cart via Shopify AJAX API
 */

if (!customElements.get('mail-it-for-you')) {
  class MailItForYou extends HTMLElement {
    constructor() {
      super();
      this.packages = [];
      this.isActive = false;
    }

    /* ---------------------------------------------------------- */
    /*  Lifecycle                                                  */
    /* ---------------------------------------------------------- */
    connectedCallback() {
      this.container    = this.querySelector('.mailit');
      this.radioInput   = this.querySelector('.mailit__radio-input');
      this.body         = this.querySelector('.mailit__body');
      this.nameInput    = this.querySelector('[data-mailit-name]');
      this.streetInput  = this.querySelector('[data-mailit-street]');
      this.cityInput    = this.querySelector('[data-mailit-city]');
      this.stateInput   = this.querySelector('[data-mailit-state]');
      this.zipInput     = this.querySelector('[data-mailit-zip]');
      this.previewEl    = this.querySelector('[data-mailit-preview]');
      this.totalCardsEl = this.querySelector('[data-mailit-total-cards]');
      this.totalPriceEl = this.querySelector('[data-mailit-total-price]');
      this.submitBtn    = this.querySelector('[data-mailit-submit]');
      this.bottomEl     = this.querySelector('[data-mailit-bottom]');

      this._initPackages();
      this._bindEvents();
      this._syncInitialPackageState();
      this._handleModuleSwitch();
      this._updateTotals();
      this._updateAddressPreview();
      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Init helpers                                               */
    /* ---------------------------------------------------------- */
    _initPackages() {
      this.querySelectorAll('.mailit__package').forEach(el => {
        this.packages.push({
          el,
          id:             parseInt(el.dataset.variantId, 10),
          price:          parseInt(el.dataset.price, 10) || 0,
          cardCount:      parseInt(el.dataset.cardCount, 10) || 0,
          defaultMessage: el.dataset.defaultMessage || '',
          checkbox:       el.querySelector('.mailit__package-checkbox'),
          messageRadios:  el.querySelectorAll('.mailit__message-check-input'),
          textarea:       el.querySelector('.mailit__message-textarea'),
          messageOptions: el.querySelectorAll('.mailit__message-option'),
        });
      });
    }

    _syncInitialPackageState() {
      this.packages.forEach(pkg => {
        if (!pkg.checkbox || !pkg.checkbox.checked) return;

        pkg.el.classList.add('mailit__package--selected');

        // Ensure message UI is aligned with checked radio (default is pre-checked in markup)
        this._handleMessageType(pkg);
      });

      // Ensure bottom summary + CTA reflect any pre-checked packages
      this._updateTotals();
      this._updateButtonState();
    }

    _bindEvents() {
      /* --- 3-way radio switching ----------------------------- */
      const radioName = this.radioInput ? this.radioInput.name : '';
      if (radioName) {
        document.querySelectorAll(`input[name="${radioName}"]`).forEach(r => {
          r.addEventListener('change', () => this._handleModuleSwitch());
        });
      }

      /* --- Per-package events -------------------------------- */
      this.packages.forEach(pkg => {
        // Checkbox
        pkg.checkbox.addEventListener('change', () => this._handlePackageToggle(pkg));

        // Message type radios
        pkg.messageRadios.forEach(r => {
          r.addEventListener('change', () => this._handleMessageType(pkg));
        });

        // Textarea live validation
        if (pkg.textarea) {
          pkg.textarea.addEventListener('input', () => this._handleTextareaInput(pkg));
        }
      });

      /* --- Address preview ----------------------------------- */
      [this.streetInput, this.cityInput, this.stateInput, this.zipInput].forEach(inp => {
        if (inp) inp.addEventListener('input', () => this._updateAddressPreview());
      });

      /* --- Submit -------------------------------------------- */
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', e => this._handleSubmit(e));
      }
    }

    /* ---------------------------------------------------------- */
    /*  Module switching (printed / digital / mail-it)             */
    /* ---------------------------------------------------------- */
    _handleModuleSwitch() {
      const selected = this.radioInput && this.radioInput.checked;
      this.isActive = selected;

      if (selected) {
        this.container.classList.add('mailit--active');

        // Hide competing modules
        const buyBtns     = document.querySelector('.dd-buy-buttons-wrapper');
        // const downloadBtn = document.querySelector('.digital-download-main .dd-download');
        const ddContent   = document.querySelector('.dd-content');
        const ddMain      = document.querySelector('.digital-download-main');

        if (buyBtns)     buyBtns.style.display     = 'none';
        // if (downloadBtn) downloadBtn.style.display  = 'none';
        if (ddContent)   ddContent.classList.remove('has-checked');
        if (ddMain)      ddMain.classList.remove('has-checked');
      } else {
        this.container.classList.remove('mailit--active');
      }
    }

    /* ---------------------------------------------------------- */
    /*  Package toggle                                             */
    /* ---------------------------------------------------------- */
    _handlePackageToggle(pkg) {
      if (pkg.checkbox.checked) {
        pkg.el.classList.add('mailit__package--selected');

        // Default to "One Voice, One Message"
        const defaultRadio = pkg.el.querySelector('.mailit__message-check-input[value="default"]');
        if (defaultRadio) defaultRadio.checked = true;
        this._handleMessageType(pkg);
      } else {
        pkg.el.classList.remove('mailit__package--selected');
        // Reset message options
        pkg.messageOptions.forEach(o => {
          o.classList.remove('mailit__message-option--active', 'mailit__message-option--error');
        });
      }

      this._updateTotals();
      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Message type switching                                     */
    /* ---------------------------------------------------------- */
    _handleMessageType(pkg) {
      const checked = pkg.el.querySelector('.mailit__message-check-input:checked');
      const value   = checked ? checked.value : 'default';

      pkg.messageOptions.forEach(opt => {
        const isMatch = opt.dataset.option === value;
        opt.classList.toggle('mailit__message-option--active', isMatch);
        opt.classList.remove('mailit__message-option--error');
      });

      // Validate "Write Your Own" immediately
      if (value === 'custom' && pkg.textarea && !pkg.textarea.value.trim()) {
        const customOpt = pkg.el.querySelector('.mailit__message-option[data-option="custom"]');
        if (customOpt) customOpt.classList.add('mailit__message-option--error');
      }

      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Textarea live validation                                   */
    /* ---------------------------------------------------------- */
    _handleTextareaInput(pkg) {
      const customOpt = pkg.el.querySelector('.mailit__message-option[data-option="custom"]');
      if (!customOpt) return;

      const text = pkg.textarea.value.trim();
      const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;

      if (text && wordCount > 1) {
        customOpt.classList.remove('mailit__message-option--error');
        customOpt.classList.add('mailit__message-option--active');
      } else {
        customOpt.classList.add('mailit__message-option--error');
      }

      this._updateButtonState();
    }

    /* ---------------------------------------------------------- */
    /*  Address preview                                            */
    /* ---------------------------------------------------------- */
    _updateAddressPreview() {
      if (!this.previewEl) return;

      const street = (this.streetInput && this.streetInput.value.trim()) || '';
      const city   = (this.cityInput   && this.cityInput.value.trim())   || '';
      const state  = (this.stateInput  && this.stateInput.value.trim())  || '';
      const zip    = (this.zipInput    && this.zipInput.value.trim())    || '';

      const parts = [];
      if (street) parts.push(street);
      if (city)   parts.push(city);

      const stateZip = [state, zip].filter(Boolean).join(' ');
      if (stateZip) parts.push(stateZip);

      const preview = parts.join(', ');
      this.previewEl.textContent = preview ? `Preview: ${preview}` : '';
    }

    /* ---------------------------------------------------------- */
    /*  Totals                                                     */
    /* ---------------------------------------------------------- */
    _updateTotals() {
      let totalCards = 0;
      let totalPrice = 0;
      let hasSelected = false;

      this.packages.forEach(pkg => {
        if (pkg.checkbox.checked) {
          hasSelected = true;
          totalCards += pkg.cardCount;
          totalPrice += pkg.price;
        }
      });

      if (this.totalCardsEl) this.totalCardsEl.textContent = totalCards;

      if (this.totalPriceEl) {
        const amount = totalPrice / 100;
        this.totalPriceEl.textContent =
          amount % 1 === 0
            ? '$' + amount.toLocaleString('en-US')
            : '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }

      // Show / hide bottom section
      if (this.bottomEl) {
        this.bottomEl.style.display = hasSelected ? '' : 'none';
      }
    }

    /* ---------------------------------------------------------- */
    /*  Button state update                                        */
    /* ---------------------------------------------------------- */
    _updateButtonState() {
      if (!this.submitBtn) return;

      const selectedPkgs = this.packages.filter(p => p.checkbox.checked);
      
      if (selectedPkgs.length === 0) {
        this.submitBtn.disabled = true;
        return;
      }

      // Check if any selected package has "Write Your Own" with insufficient words
      let shouldDisable = false;
      
      selectedPkgs.forEach(pkg => {
        const checked = pkg.el.querySelector('.mailit__message-check-input:checked');
        const msgType = checked ? checked.value : 'default';
        
        if (msgType === 'custom' && pkg.textarea) {
          const text = pkg.textarea.value.trim();
          const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
          
          if (wordCount <= 1) {
            shouldDisable = true;
          }
        }
      });

      this.submitBtn.disabled = shouldDisable;
    }

    /* ---------------------------------------------------------- */
    /*  Validation                                                 */
    /* ---------------------------------------------------------- */
    _validate() {
      let valid = true;
      const selectedPkgs = this.packages.filter(p => p.checkbox.checked);

      if (selectedPkgs.length === 0) return false;

      // Required fields
      const required = [this.nameInput, this.streetInput, this.cityInput, this.stateInput, this.zipInput];
      required.forEach(inp => {
        if (!inp || !inp.value.trim()) valid = false;
      });

      // Per-package message
      selectedPkgs.forEach(pkg => {
        const checked = pkg.el.querySelector('.mailit__message-check-input:checked');
        const type = checked ? checked.value : 'default';
        if (type === 'custom' && pkg.textarea) {
          const text = pkg.textarea.value.trim();
          const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
          if (wordCount <= 1) {
            valid = false;
          }
        }
      });

      return valid;
    }

    _highlightErrors() {
      // Input fields
      [this.nameInput, this.streetInput, this.cityInput, this.stateInput, this.zipInput].forEach(inp => {
        if (inp && !inp.value.trim()) {
          inp.classList.add('mailit__input--error');
          const handler = () => {
            if (inp.value.trim()) {
              inp.classList.remove('mailit__input--error');
              inp.removeEventListener('input', handler);
            }
          };
          inp.addEventListener('input', handler);
        }
      });

      // Package messages
      this.packages.forEach(pkg => {
        if (!pkg.checkbox.checked) return;
        const checked = pkg.el.querySelector('.mailit__message-check-input:checked');
        const type = checked ? checked.value : 'default';
        if (type === 'custom' && pkg.textarea && !pkg.textarea.value.trim()) {
          const customOpt = pkg.el.querySelector('.mailit__message-option[data-option="custom"]');
          if (customOpt) customOpt.classList.add('mailit__message-option--error');
        }
      });

      // Scroll to first error
      const firstErr = this.querySelector('.mailit__input--error, .mailit__message-option--error');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ---------------------------------------------------------- */
    /*  Add to Cart                                                */
    /* ---------------------------------------------------------- */
    async _handleSubmit(e) {
      e.preventDefault();

      if (!this._validate()) {
        this._highlightErrors();
        return;
      }

      const senderName = this.nameInput.value.trim();
      const street     = this.streetInput.value.trim();
      const city       = this.cityInput.value.trim();
      const state      = this.stateInput.value.trim();
      const zip        = this.zipInput.value.trim();
      const address    = `${street}, ${city}, ${state} ${zip}`;

      const items = this.packages
        .filter(pkg => pkg.checkbox.checked)
        .map(pkg => {
          const checked = pkg.el.querySelector('.mailit__message-check-input:checked');
          const msgType = checked ? checked.value : 'default';
          const message = msgType === 'custom'
            ? pkg.textarea.value.trim()
            : pkg.defaultMessage;

          return {
            id: pkg.id,
            quantity: 1,
            properties: {
              '_Fulfillment': 'Handwritten Mail',
              'Sender Name': senderName,
              'Sender Address': address,
              'Message Type': msgType === 'default' ? 'One Voice, One Message' : 'Write Your Own',
              'Message': message,
            },
          };
        });

      // UI → loading
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('mailit__submit--loading');
      const origText = this.submitBtn.textContent;
      this.submitBtn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }

        // Fetch updated cart data for pubsub
        const cartRes = await fetch('/cart.js');
        if (!cartRes.ok) {
          throw new Error('Could not fetch cart data');
        }
        const cartData = await cartRes.json();

        // Publish cart update event (for cart bubbles, progress bars, etc.)
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mail-it-for-you',
            cartData: cartData,
          });
        }

        // Fetch cart drawer section and update it, then open drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
          
          try {
            const sectionRes = await fetch(`${cartUrl}?section_id=cart-drawer`);
            
            if (sectionRes.ok) {
              const sectionHtml = await sectionRes.text();
              const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
              
              // Remove is-empty classes from cart drawer (if cart was empty before)
              cartDrawer.classList.remove('is-empty');
              const drawerInner = cartDrawer.querySelector('.drawer__inner');
              if (drawerInner) {
                drawerInner.classList.remove('is-empty');
              }
              
              // Remove empty state div if it exists
              const emptyState = cartDrawer.querySelector('.drawer__inner-empty');
              if (emptyState) {
                emptyState.remove();
              }
              
              // Update cart drawer items
              const drawerItems = document.querySelector('cart-drawer-items');
              const newDrawerItems = html.querySelector('cart-drawer-items');
              if (drawerItems && newDrawerItems) {
                // Remove is-empty class if present
                newDrawerItems.classList.remove('is-empty');
                drawerItems.replaceWith(newDrawerItems);
              } else if (!drawerItems && newDrawerItems) {
                // If drawer items didn't exist (empty cart), insert it
                newDrawerItems.classList.remove('is-empty');
                const drawerInner = cartDrawer.querySelector('.drawer__inner');
                if (drawerInner) {
                  const drawerHeader = drawerInner.querySelector('.drawer__header');
                  if (drawerHeader && drawerHeader.nextElementSibling) {
                    drawerHeader.nextElementSibling.insertAdjacentElement('afterend', newDrawerItems);
                  } else {
                    drawerInner.appendChild(newDrawerItems);
                  }
                }
              }
              
              // Update cart drawer footer
              const drawerFooter = document.querySelector('.cart-drawer__footer');
              const newDrawerFooter = html.querySelector('.cart-drawer__footer');
              if (drawerFooter && newDrawerFooter) {
                drawerFooter.replaceWith(newDrawerFooter);
              } else if (!drawerFooter && newDrawerFooter) {
                // If footer didn't exist, insert it
                const drawerFooterContainer = cartDrawer.querySelector('.drawer__footer');
                if (drawerFooterContainer) {
                  drawerFooterContainer.appendChild(newDrawerFooter);
                }
              }
              
              // Fetch and update cart icon bubble section separately
              // Use the same pattern as cart.js for consistency
              try {
                const bubbleRes = await fetch(`${cartUrl}?section_id=cart-icon-bubble`);
                if (bubbleRes.ok) {
                  const bubbleHtml = await bubbleRes.text();
                  const cartBubble = document.getElementById('cart-icon-bubble');
                  
                  if (cartBubble) {
                    // Extract innerHTML from the section wrapper (same as getSectionInnerHTML)
                    const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
                    const bubbleSection = bubbleDoc.querySelector('#shopify-section-cart-icon-bubble, .shopify-section');
                    
                    if (bubbleSection) {
                      cartBubble.innerHTML = bubbleSection.innerHTML;
                    } else {
                      // Fallback: try to find the content directly
                      const bubbleContent = bubbleDoc.querySelector('a#cart-icon-bubble, #cart-icon-bubble');
                      if (bubbleContent) {
                        cartBubble.innerHTML = bubbleContent.innerHTML;
                      }
                    }
                  }
                }
              } catch (bubbleErr) {
                console.warn('[mail-it-for-you] Could not update cart icon bubble:', bubbleErr);
                // Try to manually update based on cartData
                try {
                  const cartBubble = document.getElementById('cart-icon-bubble');
                  const countBubble = cartBubble?.querySelector('.cart-count-bubble');
                  if (cartBubble && cartData && cartData.item_count > 0) {
                    // Update the count if bubble exists
                    if (countBubble) {
                      const countSpan = countBubble.querySelector('span[aria-hidden="true"]');
                      if (countSpan) {
                        countSpan.textContent = cartData.item_count < 100 ? cartData.item_count : '';
                      }
                    } else {
                      // Create count bubble if it doesn't exist
                      const countDiv = document.createElement('div');
                      countDiv.className = 'cart-count-bubble';
                      if (cartData.item_count < 100) {
                        const countSpan = document.createElement('span');
                        countSpan.setAttribute('aria-hidden', 'true');
                        countSpan.textContent = cartData.item_count;
                        countDiv.appendChild(countSpan);
                      }
                      const hiddenSpan = document.createElement('span');
                      hiddenSpan.className = 'visually-hidden';
                      hiddenSpan.textContent = `Cart count: ${cartData.item_count}`;
                      countDiv.appendChild(hiddenSpan);
                      cartBubble.appendChild(countDiv);
                    }
                    // Update icon if cart was empty before
                    const svgWrapper = cartBubble.querySelector('.svg-wrapper');
                    if (svgWrapper && cartData.item_count > 0) {
                      // Icon should be cart.svg (not empty), but we can't change SVG content easily
                      // The section fetch should handle this, but this is a fallback
                    }
                  } else if (cartBubble && cartData && cartData.item_count === 0) {
                    // Remove count bubble if cart is empty
                    const countBubble = cartBubble.querySelector('.cart-count-bubble');
                    if (countBubble) {
                      countBubble.remove();
                    }
                  }
                } catch (manualErr) {
                  console.warn('[mail-it-for-you] Could not manually update cart icon bubble:', manualErr);
                }
              }
              
              // Open the drawer after a delay to ensure DOM is updated and custom elements are initialized
              setTimeout(() => {
                if (cartDrawer && typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                }
              }, 200);
            } else {
              // Fallback: redirect to cart if section fetch fails
              window.location.href = '/cart';
            }
          } catch (err) {
            console.error('[mail-it-for-you] Error updating cart drawer:', err);
            // Fallback: redirect to cart on error
            window.location.href = '/cart';
          }
        } else {
          // Fallback: redirect to cart if drawer not found
          window.location.href = '/cart';
        }

        // Reset button state
        this.submitBtn.textContent = origText;
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('mailit__submit--loading');
      } catch (err) {
        console.error('[mail-it-for-you]', err);
        this.submitBtn.textContent = 'Error – Try Again';
        setTimeout(() => {
          this.submitBtn.textContent = origText;
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('mailit__submit--loading');
        }, 2500);
      }
    }
  }

  customElements.define('mail-it-for-you', MailItForYou);
}

